import * as icons from '@mui/icons-material';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers-pro';
import moment from 'moment-timezone';
import { FC, ReactNode, RefObject } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface InterveneButtonParams {
  onClickInterveneButton: () => void;
  sendingSms: boolean;
  loading: boolean;
  phone: string;
  interveneHours: number | null;
  onClearIntervene: () => void;
  showInterveneMenu: boolean;
  menuRef: RefObject<HTMLDivElement>;
  createButton: (hours: number) => ReactNode;
}

// ============================================================================
// Components
// ============================================================================

export const ScheduledTimePicker: FC<{
  scheduledTime: Date | null;
  setScheduledTime: (value: ((prevState: Date | null) => Date | null) | Date | null) => void;
  leadTimeZone: string;
}> = ({ scheduledTime, setScheduledTime, leadTimeZone }) => (
  <Box padding={2} paddingBottom={0}>
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateTimePicker
        label="Schedule message for"
        value={scheduledTime}
        onChange={(newValue) => setScheduledTime(newValue)}
        timezone={leadTimeZone}
        disablePast
        slotProps={{
          textField: {
            size: 'small',
            fullWidth: true,
            helperText: `Message will be sent at the selected time (${leadTimeZone}). Currently it is ${moment()
              .tz(leadTimeZone)
              .format('h:mm A z')}.`,
          },
        }}
        sx={{ width: '100%' }}
      />
    </LocalizationProvider>
  </Box>
);

export const NewMessageField: FC<{
  value: string;
  onChange: (message: string) => void;
  sendingSms: boolean;
  loading: boolean;
  phone: string;
}> = ({ loading, onChange, phone, sendingSms, value }) => {
  return (
    <TextField
      fullWidth
      multiline
      variant="outlined"
      placeholder="Type a message..."
      size="small"
      onChange={(e) => onChange(e.target.value)}
      disabled={sendingSms || loading || !phone}
      value={value}
    />
  );
};

export const InterveneButton: FC<InterveneButtonParams> = ({
  createButton,
  interveneHours,
  loading,
  onClearIntervene,
  onClickInterveneButton,
  phone,
  menuRef,
  sendingSms,
  showInterveneMenu,
}) => (
  <Box sx={{ position: 'relative' }}>
    <Box sx={{ position: 'relative' }}>
      <IconButton
        size="medium"
        onClick={onClickInterveneButton}
        disabled={sendingSms || loading || !phone}
        color="primary"
        sx={{
          border: '1px solid rgba(25, 118, 210, 0.5)',
          borderRadius: '4px',
          height: '36px',
          width: '36px',
          '&:hover': {
            border: '1px solid #1976d2',
            bgcolor: 'rgba(0, 0, 0, 0.18)',
          },
        }}
      >
        <icons.CommentsDisabled />
        {interveneHours && (
          <Box
            sx={{
              position: 'absolute',
              bottom: '-2px',
              right: '-2px',
              bgcolor: '#1976d2',
              color: 'white',
              borderRadius: '50%',
              width: '16px',
              height: '16px',
              fontSize: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
            }}
          >
            {interveneHours}
          </Box>
        )}
      </IconButton>
      {interveneHours && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onClearIntervene();
          }}
          sx={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            width: '16px',
            height: '16px',
            bgcolor: '#f44336',
            color: 'white',
            '&:hover': {
              bgcolor: '#d32f2f',
            },
          }}
        >
          <icons.Close fontSize="small" sx={{ fontSize: '12px' }} />
        </IconButton>
      )}
    </Box>
    {showInterveneMenu && (
      <Box
        ref={menuRef}
        sx={{
          position: 'absolute',
          bottom: '40px',
          right: 0,
          bgcolor: 'background.paper',
          boxShadow: 3,
          borderRadius: 1,
          zIndex: 1000,
          width: '130px',
        }}
      >
        <Typography
          padding={1}
          sx={{
            color: '#1976d2',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            fontSize: '0.75rem',
          }}
        >
          Intervene for
        </Typography>
        {[1, 2, 4, 8, 12, 24].map(createButton)}
      </Box>
    )}
  </Box>
);
